const DARK_SURFACE = '#111111';
const DARK_PANEL = '#161616';
const DARK_BORDER = '#2f2f2f';
const DARK_TEXT = '#f8fafc';
const HEADER_ACCENT = '#036dda';
const LIGHT_SURFACE = '#ffffff';
const LIGHT_TEXT = '#111827';
export const EMAIL_EDITOR_MIN_HEIGHT = 560;

const LIGHT_BACKGROUND_VALUES = new Set([
    '#fff',
    '#ffffff',
    '#fffffe',
    'white',
    'rgb(255,255,255)',
    'rgb(255, 255, 255)',
    'rgba(255,255,255,1)',
    'rgba(255, 255, 255, 1)',
    'transparent',
]);

const DARK_TEXT_VALUES = new Set([
    '#000',
    '#000000',
    '#111',
    '#111111',
    '#222',
    '#222222',
    'black',
    'rgb(0,0,0)',
    'rgb(0, 0, 0)',
    'rgba(0,0,0,1)',
    'rgba(0, 0, 0, 1)',
]);

const TEXTUAL_BLOCK_TYPES = new Set(['text', 'heading', 'button', 'menu', 'html']);

const normalizeColorValue = (value) => {
    if (typeof value !== 'string') {
        return '';
    }

    return value.trim().toLowerCase();
};

const shouldFlipLightBackground = (value) => LIGHT_BACKGROUND_VALUES.has(normalizeColorValue(value));

const shouldFlipDarkText = (value) => DARK_TEXT_VALUES.has(normalizeColorValue(value));

const forceEditableFlags = (node) => {
    if (Array.isArray(node)) {
        node.forEach(forceEditableFlags);
        return;
    }

    if (!node || typeof node !== 'object') {
        return;
    }

    ['selectable', 'draggable', 'duplicatable', 'deletable', 'hideable'].forEach((key) => {
        if (key in node) {
            node[key] = true;
        }
    });

    Object.values(node).forEach(forceEditableFlags);
};

const applyDarkThemeToValues = (values = {}, type = '') => {
    const nextValues = { ...values };

    if (type === 'heading') {
        nextValues.textColor = HEADER_ACCENT;
        nextValues.color = HEADER_ACCENT;
        nextValues.linkColor = HEADER_ACCENT;
    }

    ['backgroundColor', 'contentBackgroundColor', 'bodyBackgroundColor'].forEach((key) => {
        if (!nextValues[key] || shouldFlipLightBackground(nextValues[key])) {
            nextValues[key] = DARK_SURFACE;
        }
    });

    if (TEXTUAL_BLOCK_TYPES.has(type)) {
        ['textColor', 'color'].forEach((key) => {
            if (!nextValues[key] || shouldFlipDarkText(nextValues[key])) {
                nextValues[key] = type === 'heading' ? HEADER_ACCENT : DARK_TEXT;
            }
        });

        if (!nextValues.linkColor || shouldFlipDarkText(nextValues.linkColor)) {
            nextValues.linkColor = type === 'heading' ? HEADER_ACCENT : DARK_TEXT;
        }
    }

    return nextValues;
};

const walkAndApplyDarkTheme = (node) => {
    if (Array.isArray(node)) {
        node.forEach(walkAndApplyDarkTheme);
        return;
    }

    if (!node || typeof node !== 'object') {
        return;
    }

    if (node.values && typeof node.values === 'object') {
        node.values = applyDarkThemeToValues(node.values, node.type);
    }

    Object.values(node).forEach(walkAndApplyDarkTheme);
};

const cloneDesign = (design) => JSON.parse(JSON.stringify(design));

export const getEmailEditorAppearance = (darkMode) => ({
    theme: darkMode ? 'modern_dark' : 'modern_light',
    panels: {
        tools: {
            dock: 'right',
        },
    },
});

export const getEmailEditorFrameStyle = (darkMode) => ({
    backgroundColor: darkMode ? DARK_PANEL : LIGHT_SURFACE,
    border: `1px solid ${darkMode ? DARK_BORDER : '#d1d5db'}`,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: darkMode
        ? '0 20px 45px rgba(0, 0, 0, 0.35)'
        : '0 12px 32px rgba(15, 23, 42, 0.08)',
});

export const getEmailEditorSurfaceStyles = (darkMode, scopeClassName) => `
    .${scopeClassName} {
        background: ${darkMode ? DARK_PANEL : '#f8fafc'};
        padding: 10px;
        border-radius: 14px;
    }

    .${scopeClassName} > div {
        min-height: ${EMAIL_EDITOR_MIN_HEIGHT}px;
    }

    .${scopeClassName} iframe {
        background: ${darkMode ? DARK_SURFACE : LIGHT_SURFACE};
    }

    .${scopeClassName} .blockbuilder-layer-selector,
    .${scopeClassName} .u_row,
    .${scopeClassName} .u_content_row,
    .${scopeClassName} .tab-container,
    .${scopeClassName} .main-editor {
        background-color: ${darkMode ? DARK_SURFACE : LIGHT_SURFACE} !important;
        color: ${darkMode ? DARK_TEXT : LIGHT_TEXT} !important;
    }
`;

export const prepareEmailDesignForEditor = (templateDesign, darkMode) => {
    if (!templateDesign) {
        return null;
    }

    const parsedDesign = typeof templateDesign === 'string'
        ? JSON.parse(templateDesign)
        : cloneDesign(templateDesign);

    const nextDesign = cloneDesign(parsedDesign);

    forceEditableFlags(nextDesign);

    const applyHeadingAccent = (node) => {
        if (Array.isArray(node)) {
            node.forEach(applyHeadingAccent);
            return;
        }

        if (!node || typeof node !== 'object') {
            return;
        }

        if (node.type === 'heading' && node.values && typeof node.values === 'object') {
            node.values = {
                ...node.values,
                textColor: HEADER_ACCENT,
                color: HEADER_ACCENT,
                linkColor: HEADER_ACCENT,
            };
        }

        Object.values(node).forEach(applyHeadingAccent);
    };

    applyHeadingAccent(nextDesign);

    if (darkMode) {
        walkAndApplyDarkTheme(nextDesign);
    }

    return nextDesign;
};
